import React, { useEffect, useState } from 'react';
import {
  Plus,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Tv,
  Globe2,
  Share2,
  AlertCircle,
  FileText,
  Radio,
  User as UserIcon,
  Flag,
  CheckCircle2,
  Clock,
  XCircle,
  Pencil,
  X,
  Target,
  ArrowRight,
} from 'lucide-react';
import {
  getStories,
  createStory,
  createPlanningItem,
  getPlanningItems,
  updatePlanningItem,
  startWorkingOnAssignment,
  getContentItemsByStory,
  getCurrentUser,
  getUsers,
} from '../services/store';
import {
  Story,
  PlanningItem,
  PlanningItemWithUser,
  ContentItemWithVersion,
  User,
  Desk,
  Platform,
  LanguageCode,
  ItemType,
  PlanningStatus,
  LANGUAGE_LABELS,
  WORKFLOW_STATUS_LABELS,
  WORKFLOW_STATUS_COLORS,
  getUrgencyLabel,
  DEFAULT_LANGUAGE,
  DEFAULT_PLATFORM,
} from '../types';
import { supabase } from '../services/supabaseClient';

interface StoryWithPlanning extends Story {
  planning_items?: PlanningItemWithUser[];
  content_items?: ContentItemWithVersion[];
}

interface PlanningProps {
  onEditStory?: (storyId: string) => void;
}

const Planning: React.FC<PlanningProps> = ({ onEditStory }) => {
  const [stories, setStories] = useState<StoryWithPlanning[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showNewStoryForm, setShowNewStoryForm] = useState(false);
  const [showNewPlanningForm, setShowNewPlanningForm] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [refresh, setRefresh] = useState(0);

  // New story form state
  const [newSlug, setNewSlug] = useState('');
  const [newUrgency, setNewUrgency] = useState(5);
  const [newIsBreaking, setNewIsBreaking] = useState(false);

  // New planning item form state
  const [newPlanningDesk, setNewPlanningDesk] = useState<Desk>(DEFAULT_PLATFORM as Desk);
  const [newPlanningPlatform, setNewPlanningPlatform] = useState<Platform>(DEFAULT_PLATFORM);
  const [newPlanningLanguage, setNewPlanningLanguage] = useState<LanguageCode>(DEFAULT_LANGUAGE);
  const [newPlanningType, setNewPlanningType] = useState<ItemType>('text');
  const [newPlanningAssignee, setNewPlanningAssignee] = useState<string>('');
  const [newPlanningDueDate, setNewPlanningDueDate] = useState('');
  const [newPlanningNotes, setNewPlanningNotes] = useState('');
  const [newPlanningPriority, setNewPlanningPriority] = useState(50);
  const [allUsers, setAllUsers] = useState<User[]>([]);

  useEffect(() => {
    loadData();
  }, [refresh, selectedDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [user, fetchedStories, users] = await Promise.all([
        getCurrentUser(),
        getStories({ limit: 100 }),
        getUsers(),
      ]);

      setCurrentUser(user);
      setAllUsers(users);

      // Load planning items and content items for each story
      const storiesWithData = await Promise.all(
        fetchedStories.map(async (story) => {
          const [planningItems, contentItems] = await Promise.all([
            getPlanningItems(story.id),
            getContentItemsByStory(story.id),
          ]);
          return {
            ...story,
            planning_items: planningItems,
            content_items: contentItems,
          };
        })
      );

      setStories(storiesWithData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStory = async () => {
    if (!newSlug) return;

    await createStory({
      slug: newSlug,
      urgency: newUrgency,
      is_breaking: newIsBreaking,
    });

    setNewSlug('');
    setNewUrgency(5);
    setNewIsBreaking(false);
    setShowNewStoryForm(false);
    setRefresh((prev) => prev + 1);
  };

  const handleCreatePlanningItem = async (storyId: string) => {
    // Auto-detect item type from platform
    const itemType: ItemType = newPlanningPlatform === 'tv' ? 'video'
      : newPlanningPlatform === 'radio' ? 'audio'
      : 'text';

    await createPlanningItem({
      story_id: storyId,
      platform: newPlanningPlatform,
      language: newPlanningLanguage,
      assigned_desk: newPlanningDesk,
      item_type: itemType,
      assigned_to: newPlanningAssignee || undefined,
      due_date: newPlanningDueDate || undefined,
      priority: newPlanningPriority,
      notes: newPlanningNotes || undefined,
    });

    // Reset form
    setNewPlanningDesk('tv');
    setNewPlanningPlatform('tv');
    setNewPlanningLanguage('fa-AF');
    setNewPlanningType('text');
    setNewPlanningAssignee('');
    setNewPlanningDueDate('');
    setNewPlanningNotes('');
    setNewPlanningPriority(50);
    setShowNewPlanningForm(null);
    setRefresh((prev) => prev + 1);
  };

  const handleStartWorking = async (planningId: string) => {
    await startWorkingOnAssignment(planningId);
    setRefresh((prev) => prev + 1);
  };

  const handleUpdatePlanningStatus = async (id: string, status: PlanningStatus) => {
    await updatePlanningItem(id, { status });
    setRefresh((prev) => prev + 1);
  };

  const changeDate = (delta: number) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + delta);
    setSelectedDate(current.toISOString().split('T')[0]);
  };

  const getDeskIcon = (desk: Desk) => {
    switch (desk) {
      case 'tv':
        return <Tv className="h-4 w-4" />;
      case 'radio':
        return <Radio className="h-4 w-4" />;
      case 'web':
        return <Globe2 className="h-4 w-4" />;
      case 'social':
        return <Share2 className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getDeskColor = (desk: Desk) => {
    switch (desk) {
      case 'tv':
        return 'text-blue-500 bg-blue-50 dark:bg-blue-900/20';
      case 'radio':
        return 'text-orange-500 bg-orange-50 dark:bg-orange-900/20';
      case 'web':
        return 'text-green-500 bg-green-50 dark:bg-green-900/20';
      case 'social':
        return 'text-purple-500 bg-purple-50 dark:bg-purple-900/20';
      default:
        return 'text-zinc-500 bg-zinc-50 dark:bg-zinc-800';
    }
  };

  const getPlanningStatusBadge = (status: PlanningStatus) => {
    const configs: Record<PlanningStatus, { color: string; label: string }> = {
      planned: { color: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300', label: 'Planned' },
      confirming: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', label: 'Confirming' },
      assigned: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'Assigned' },
      in_progress: { color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', label: 'In Progress' },
      covered: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Covered' },
      canceled: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Canceled' },
    };

    const config = configs[status] || configs.planned;
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const formattedDate = new Date(selectedDate).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="p-8 max-w-7xl mx-auto h-full overflow-y-auto animate-fade-in">
      {/* Header with Date Navigation */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-white">Editorial Planning</h1>
          <p className="text-zinc-500 dark:text-zinc-400 mt-1">
            Plan stories and assign content production
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 shadow-sm">
            <button
              onClick={() => changeDate(-1)}
              className="p-2 hover:bg-zinc-50 dark:hover:bg-zinc-800 rounded-l-lg transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
            </button>
            <div className="px-4 py-2 flex items-center min-w-[200px] justify-center">
              <Calendar className="h-4 w-4 text-zinc-400 mr-2" />
              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                {formattedDate}
              </span>
            </div>
            <button
              onClick={() => changeDate(1)}
              className="p-2 hover:bg-zinc-50 dark:hover:bg-zinc-800 rounded-r-lg transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
            </button>
          </div>
          <button
            onClick={() => setShowNewStoryForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium"
          >
            <Plus className="h-4 w-4 mr-2" /> New Story
          </button>
        </div>
      </div>

      {/* New Story Form */}
      {showNewStoryForm && (
        <div className="mb-8 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-4">
            Create New Story
          </h3>
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                Story Slug *
              </label>
              <input
                type="text"
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value.toUpperCase().replace(/\s+/g, '-'))}
                className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono"
                placeholder="e.g., AFG-KABUL-ELECTION-2025"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                Urgency: {getUrgencyLabel(newUrgency)}
              </label>
              <input
                type="range"
                min="1"
                max="9"
                value={newUrgency}
                onChange={(e) => setNewUrgency(Number(e.target.value))}
                className="w-full mt-2"
              />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={newIsBreaking}
                onChange={(e) => setNewIsBreaking(e.target.checked)}
                className="rounded border-zinc-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">Breaking News</span>
            </label>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowNewStoryForm(false)}
              className="px-4 py-2 text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateStory}
              disabled={!newSlug}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-zinc-300 disabled:cursor-not-allowed transition-colors"
            >
              Create Story
            </button>
          </div>
        </div>
      )}

      {/* Stories List */}
      {stories.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-zinc-900 rounded-xl border border-dashed border-zinc-300 dark:border-zinc-700">
          <div className="bg-zinc-50 dark:bg-zinc-800 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-zinc-400" />
          </div>
          <h3 className="text-lg font-medium text-zinc-900 dark:text-white">
            No stories in the system
          </h3>
          <p className="text-zinc-500 dark:text-zinc-400 mb-6">
            Create your first story to start planning content.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {stories.map((story) => (
            <div
              key={story.id}
              className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 shadow-sm overflow-hidden"
            >
              {/* Story Header */}
              <div className="p-4 border-b border-zinc-100 dark:border-zinc-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-1 h-10 rounded-full"
                      style={{
                        backgroundColor:
                          story.urgency <= 2 ? '#EF4444' : story.urgency <= 4 ? '#F97316' : '#10B981',
                      }}
                    />
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-mono font-bold text-zinc-900 dark:text-white text-lg">
                          {story.slug}
                        </span>
                        {story.is_breaking && (
                          <span className="px-2 py-0.5 text-xs font-bold rounded bg-red-600 text-white animate-pulse">
                            BREAKING
                          </span>
                        )}
                        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-zinc-200 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 rounded">
                          {LANGUAGE_LABELS[story.canonical_language]}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                        {getUrgencyLabel(story.urgency)} | {story.planning_items?.length || 0} planned | {story.content_items?.length || 0} content items
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onEditStory?.(story.id)}
                      className="flex items-center px-3 py-1.5 text-sm text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
                      title="Edit story in Story Editor"
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit Story
                    </button>
                    <button
                      onClick={() => setShowNewPlanningForm(story.id)}
                      className="flex items-center px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Assignment
                    </button>
                  </div>
                </div>
              </div>

              {/* New Planning Item Form (Assignment) */}
              {showNewPlanningForm === story.id && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-b border-blue-100 dark:border-blue-900/30">
                  <h4 className="text-sm font-medium text-zinc-900 dark:text-white mb-3">
                    Create Assignment
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
                        Platform *
                      </label>
                      <select
                        value={newPlanningPlatform}
                        onChange={(e) => {
                          const platform = e.target.value as Platform;
                          setNewPlanningPlatform(platform);
                          setNewPlanningDesk(platform as Desk);
                        }}
                        className="w-full px-2 py-1.5 text-sm border border-zinc-200 dark:border-zinc-700 rounded bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
                      >
                        <option value="tv">TV</option>
                        <option value="radio">Radio</option>
                        <option value="web">Web</option>
                        <option value="social">Social</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
                        Language *
                      </label>
                      <select
                        value={newPlanningLanguage}
                        onChange={(e) => setNewPlanningLanguage(e.target.value as LanguageCode)}
                        className="w-full px-2 py-1.5 text-sm border border-zinc-200 dark:border-zinc-700 rounded bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
                      >
                        <option value="fa-AF">Dari</option>
                        <option value="ps-AF">Pashto</option>
                        <option value="eng">English</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
                        Assign To
                      </label>
                      <select
                        value={newPlanningAssignee}
                        onChange={(e) => setNewPlanningAssignee(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-zinc-200 dark:border-zinc-700 rounded bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
                      >
                        <option value="">Unassigned</option>
                        {allUsers.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.full_name || user.email}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
                        Deadline
                      </label>
                      <input
                        type="datetime-local"
                        value={newPlanningDueDate}
                        onChange={(e) => setNewPlanningDueDate(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-zinc-200 dark:border-zinc-700 rounded bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
                        Priority (1-100)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={newPlanningPriority}
                        onChange={(e) => setNewPlanningPriority(Number(e.target.value))}
                        className="w-full px-2 py-1.5 text-sm border border-zinc-200 dark:border-zinc-700 rounded bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
                        Notes
                      </label>
                      <input
                        type="text"
                        value={newPlanningNotes}
                        onChange={(e) => setNewPlanningNotes(e.target.value)}
                        placeholder="Assignment notes..."
                        className="w-full px-2 py-1.5 text-sm border border-zinc-200 dark:border-zinc-700 rounded bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2 mt-3">
                    <button
                      onClick={() => setShowNewPlanningForm(null)}
                      className="px-3 py-1.5 text-sm text-zinc-600 dark:text-zinc-400"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleCreatePlanningItem(story.id)}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Create Assignment
                    </button>
                  </div>
                </div>
              )}

              {/* Planning Items (Assignments) */}
              {story.planning_items && story.planning_items.length > 0 && (
                <div className="p-4">
                  <div className="space-y-2">
                    {story.planning_items.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <span className={`p-2 rounded-lg ${getDeskColor(item.assigned_desk)}`}>
                            {getDeskIcon(item.assigned_desk)}
                          </span>
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-zinc-900 dark:text-white">
                                {(item.platform || item.assigned_desk).toUpperCase()} ({LANGUAGE_LABELS[item.language || 'fa-AF']})
                              </span>
                              {getPlanningStatusBadge(item.status)}
                            </div>
                            <div className="flex items-center space-x-2 mt-0.5">
                              {item.assigned_user && (
                                <span className="text-xs text-blue-600 dark:text-blue-400">
                                  <UserIcon className="h-3 w-3 inline mr-1" />
                                  {item.assigned_user.full_name || item.assigned_user.email}
                                </span>
                              )}
                              {item.due_date && (
                                <span className="text-xs text-zinc-500 dark:text-zinc-400">
                                  <Clock className="h-3 w-3 inline mr-1" />
                                  {new Date(item.due_date).toLocaleString('en-US', {
                                    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                                  })}
                                </span>
                              )}
                              {item.notes && (
                                <span className="text-xs text-zinc-500 dark:text-zinc-400">
                                  {item.notes}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {item.status === 'planned' && !item.assigned_to && (
                            <button
                              onClick={() => handleUpdatePlanningStatus(item.id, 'assigned')}
                              className="px-2 py-1 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50"
                            >
                              Assign
                            </button>
                          )}
                          {(item.status === 'assigned' || item.status === 'planned') && !item.promoted_to_content_at && (
                            <button
                              onClick={() => handleStartWorking(item.id)}
                              className="flex items-center px-2 py-1 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded hover:bg-green-200 dark:hover:bg-green-900/50"
                            >
                              <Pencil className="h-3 w-3 mr-1" />
                              Start Working
                            </button>
                          )}
                          {item.promoted_to_content_at && (
                            <button
                              onClick={() => onEditStory?.(story.id)}
                              className="flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                              title="Edit content in Story Editor"
                            >
                              <Pencil className="h-3 w-3 mr-1" />
                              Edit Content
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Content Items Summary */}
              {story.content_items && story.content_items.length > 0 && (
                <div className="px-4 pb-4">
                  <p className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-2">
                    Content Items
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {story.content_items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => onEditStory?.(story.id)}
                        className="flex items-center space-x-1.5 px-2 py-1 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg text-xs hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors cursor-pointer"
                        title="Click to edit in Story Editor"
                      >
                        <span className={getDeskColor(item.platform as Desk).split(' ')[0]}>
                          {getDeskIcon(item.platform as Desk)}
                        </span>
                        <span className="text-zinc-700 dark:text-zinc-300">
                          {item.platform.toUpperCase()}
                        </span>
                        <span className="text-zinc-400">|</span>
                        <span className="text-zinc-500">{LANGUAGE_LABELS[item.language]}</span>
                        <span
                          className="px-1 py-0.5 rounded text-[10px] font-medium"
                          style={{
                            backgroundColor: `${WORKFLOW_STATUS_COLORS[item.workflow_status]}20`,
                            color: WORKFLOW_STATUS_COLORS[item.workflow_status],
                          }}
                        >
                          {WORKFLOW_STATUS_LABELS[item.workflow_status]}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Planning;
